import { Badge, Link as FluentLink, mergeClasses, Subtitle1 } from '@fluentui/react-components';
import { LinkMultiple24Regular } from '@fluentui/react-icons';
import { Bubble, ControlledField, TabContent } from '@shared/components';
import { TERMINOLOGY } from '@shared/i18n';
import { UseFormReturn } from 'react-hook-form';
import { TFormData } from './InlineBlockContainer';
import { TInlineBlock } from '../types';
import classNames from 'classnames';
import { stripProtocol } from '@shared/utils';
import { LibraryIcon } from '@shared/icons';
import { useEffect, useRef } from 'react';

type TProps = {
  inlineBlock: TInlineBlock;
  isEdit: boolean;
  formData: UseFormReturn<TFormData, any, undefined>;
  flushAutosave: () => void;
  showUrlField: boolean;
};

const InlineBlockEditor = ({ formData, inlineBlock, isEdit, flushAutosave, showUrlField }: TProps) => {
  const valueInputRef = useRef<HTMLTextAreaElement | null>(null);
  const urlValue = formData.watch('url');
  const hasUrl = !!urlValue || showUrlField;
  const variant = hasUrl ? 'url' : inlineBlock.isQm ? 'qm' : 'default';

  useEffect(() => {
    if (!isEdit) return;

    const timeoutId = window.setTimeout(() => {
      const input = valueInputRef.current;
      if (!input) return;

      input.focus();
      const valueLength = input.value.length;
      input.setSelectionRange(valueLength, valueLength);
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [isEdit]);

  return (
    <TabContent>
      <Bubble>
        <div className="flex flex-col items-center space-y-6 my-12 pb-2">
          <Badge
            icon={<LibraryIcon variant={variant} filled={isEdit} fontSize="24px" className="text-brand-80" />}
            appearance="ghost"
            color="important"
          >
            <Subtitle1>{TERMINOLOGY.inlineBlock.upper}</Subtitle1>
          </Badge>

          <form className="h-full w-full flex flex-col items-center space-y-3" onSubmit={(e) => e.preventDefault()}>
            <ControlledField
              ref={valueInputRef}
              name="value"
              control={formData.control}
              errors={formData.formState.errors}
              inputType="textarea"
              placeholder="Type the value here..."
              fieldClassName="w-full max-w-2xl"
              tooltip={!isEdit ? `Click "${inlineBlock.draft ? 'Edit draft' : 'Create draft'}" to start editing.` : ''}
              tooltipPosition="below"
              tooltipWithArrow={true}
              required={true}
              readonly={!isEdit}
              autoFocus={true}
              autoResize={true}
              onBlur={flushAutosave} // save immediately on focus-out
            />

            {/* URL field - shown when toggled via NavOverview */}
            {showUrlField && (
              // In view mode, add pt-1 to compensate for the ::after border indicator
              // that Fluent UI's Textarea renders in edit mode (strokeWidthThick).
              <div className={classNames('w-full max-w-2xl', { 'pt-1 pl-[11px]': !isEdit })}>
                {isEdit ? (
                  <ControlledField
                    name="url"
                    control={formData.control}
                    errors={formData.formState.errors}
                    inputType="input"
                    placeholder="app.confideck.com"
                    fieldClassName="w-full"
                    readonly={!isEdit}
                    contentBefore={<LinkMultiple24Regular />}
                    onBlur={flushAutosave}
                  />
                ) : (
                  urlValue && (
                    <div className="flex items-center gap-1">
                      <LinkMultiple24Regular className="shrink-0" />
                      <FluentLink
                        href={urlValue}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={mergeClasses('external-link', 'force-break-word')}
                      >
                        {stripProtocol(urlValue)}
                      </FluentLink>
                    </div>
                  )
                )}
              </div>
            )}
          </form>
        </div>
      </Bubble>
    </TabContent>
  );
};

export default InlineBlockEditor;
